import {create} from "zustand";

import type {BoardOfDirectorsSeat} from "../board-of-directors/types";
import {mapPropertyCitationTypesTowableFromApi} from "../lib/citation-towable";
import {PropertyForm} from "../types";

type DetailsStoreState = {
  initialProperty: PropertyForm | null;
  property: PropertyForm | null;
  initialBoardOfDirectors: BoardOfDirectorsSeat[] | null;
  boardOfDirectors: BoardOfDirectorsSeat[] | null;
};

type DetailsStoreActions = {
  initialize: (property: Omit<PropertyForm, "gate_codes_parsed">) => void;
  setProperty: (property: PropertyForm) => void;
  initializeBoardOfDirectors: (seats: BoardOfDirectorsSeat[]) => void;
  setBoardOfDirectors: (seats: BoardOfDirectorsSeat[]) => void;
  clearProperty: () => void;
};

function cloneBoardSeats(seats: BoardOfDirectorsSeat[]): BoardOfDirectorsSeat[] {
  return structuredClone(seats);
}

export const useDetailsStore = create<DetailsStoreState & DetailsStoreActions>((set) => ({
  initialProperty: null,
  property: null,
  initialBoardOfDirectors: null,
  boardOfDirectors: null,

  initialize: (property: Omit<PropertyForm, "gate_codes_parsed">) => {
    const gate_codes_parsed = property.gate_codes ? JSON.parse(property.gate_codes) : [];
    const citation_types = mapPropertyCitationTypesTowableFromApi(property.citation_types);
    set({
      initialProperty: structuredClone({
        ...property,
        gate_codes_parsed,
        citation_types,
      }),
      property: {
        ...property,
        gate_codes_parsed,
        citation_types,
      },
    });
  },
  setProperty: (property: PropertyForm) =>
    set({
      property: structuredClone(property),
    }),
  initializeBoardOfDirectors: (seats: BoardOfDirectorsSeat[]) => {
    const cloned = cloneBoardSeats(seats);
    set({
      initialBoardOfDirectors: cloned,
      boardOfDirectors: cloneBoardSeats(seats),
    });
  },
  setBoardOfDirectors: (seats: BoardOfDirectorsSeat[]) =>
    set({
      boardOfDirectors: cloneBoardSeats(seats),
    }),
  clearProperty: () =>
    set({
      property: null,
      initialProperty: null,
      initialBoardOfDirectors: null,
      boardOfDirectors: null,
    }),
}));

export function useDetailsStoreProperty() {
  return useDetailsStore((state) => state.property);
}

export function useDetailsStoreInitialProperty() {
  return useDetailsStore((state) => state.initialProperty);
}

export function useDetailsStoreBoardOfDirectors() {
  return useDetailsStore((state) => state.boardOfDirectors);
}

export function useDetailsStoreInitialBoardOfDirectors() {
  return useDetailsStore((state) => state.initialBoardOfDirectors);
}

export function useDetailsStoreInitialize() {
  return useDetailsStore((state) => state.initialize);
}

export function useDetailsStoreInitializeBoardOfDirectors() {
  return useDetailsStore((state) => state.initializeBoardOfDirectors);
}

export function useDetailsStoreSetProperty() {
  return useDetailsStore((state) => state.setProperty);
}

export function useDetailsStoreSetBoardOfDirectors() {
  return useDetailsStore((state) => state.setBoardOfDirectors);
}

export function useDetailsStoreClearProperty() {
  return useDetailsStore((state) => state.clearProperty);
}
